import { Button, Modal, Tag, message } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { THESIS_PLANS, getThesisPlan, type ThesisPlanId } from '@/constants/thesisPlans'
import {
  formatQuotaWan,
  isSignedIn,
  isSubActive,
  useSubscriptionStore,
} from '@/stores/subscriptionStore'
import { BRAND } from '@/constants/brand'
import {
  fetchCheckoutUrl,
  fetchCodepayStatus,
  isThesisCloudAuth,
} from '@/lib/thesisApi'

const PricingModal = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const open = searchParams.get('modal[pricing]') === '1'
  const profile = useSubscriptionStore((s) => s.profile)
  const sub = useSubscriptionStore((s) => s.sub)
  const purchase = useSubscriptionStore((s) => s.purchase)
  const refreshMe = useSubscriptionStore((s) => s.refreshMe)
  const signedIn = isSignedIn(profile)
  const [codepayOk, setCodepayOk] = useState(false)
  const [buying, setBuying] = useState<string | null>(null)

  useEffect(() => {
    if (!isThesisCloudAuth()) return
    void fetchCodepayStatus().then((r) => {
      if (r.ok) setCodepayOk(!!r.data.ok)
    })
  }, [open])

  // 支付回跳：?paid=1 → 拉云端额度；演示模式仍支持 ?paid=<planId>
  useEffect(() => {
    const paid = searchParams.get('paid')
    if (!paid) return

    const finish = async () => {
      if (!isSignedIn(useSubscriptionStore.getState().profile)) {
        searchParams.set('modal[auth]', '1')
        setSearchParams(searchParams)
        return
      }
      if (isThesisCloudAuth() && useSubscriptionStore.getState().token) {
        message.loading({ content: '正在确认支付…', key: 'paid', duration: 0 })
        for (let i = 0; i < 8; i++) {
          await refreshMe()
          await new Promise((r) => setTimeout(r, 800))
          const cur = useSubscriptionStore.getState().sub
          if (cur && cur.planId !== 'trial' && isSubActive(cur)) break
        }
        message.success({ content: '已同步套餐额度', key: 'paid' })
      } else if (THESIS_PLANS.some((p) => p.id === paid)) {
        const next = purchase(paid as ThesisPlanId)
        message.success(`已开通「${getThesisPlan(paid as ThesisPlanId).name}」，单号 ${next.orderId}`)
      }
      searchParams.delete('paid')
      searchParams.set('modal[pricing]', '1')
      setSearchParams(searchParams)
    }
    void finish()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- one-shot on mount

  const close = () => {
    searchParams.delete('modal[pricing]')
    setSearchParams(searchParams)
  }

  const buy = async (planId: ThesisPlanId) => {
    if (!signedIn) {
      searchParams.delete('modal[pricing]')
      searchParams.set('modal[auth]', '1')
      setSearchParams(searchParams)
      return
    }

    if (isThesisCloudAuth() && codepayOk) {
      setBuying(planId)
      try {
        const returnUrl = `${BRAND.url}/?paid=1`
        const r = await fetchCheckoutUrl({
          plan: planId,
          email: profile.email,
          returnUrl,
          type: 'alipay',
        })
        if (!r.ok) {
          message.error(r.error)
          return
        }
        const win = window.open(r.data.url, '_blank', 'noopener,noreferrer')
        if (!win) {
          message.warning('浏览器拦截了新标签页，请允许弹窗后重试')
          return
        }
        message.info('已在新标签页打开支付，完成后回到本页即可（可点右上角刷新额度）')
      } finally {
        setBuying(null)
      }
      return
    }

    // 未接码支付：本机演示开通
    const next = purchase(planId)
    message.success(`已开通「${getThesisPlan(planId).name}」，单号 ${next.orderId}`)
  }

  const active = isSubActive(sub)
  const current = sub ? getThesisPlan(sub.planId) : null
  const gift = getThesisPlan('trial')

  return (
    <Modal
      title="套餐与额度"
      open={open}
      onCancel={close}
      footer={null}
      width={720}
      destroyOnClose
    >
      {current && (
        <div className="thesis-panel mb-4 px-4 py-3 text-sm">
          当前：<strong>{current.name}</strong>
          {active ? (
            <>
              {' '}
              · 剩余约 {formatQuotaWan(sub!.remainingTokens)} Token
              {sub!.expiresAt
                ? ` · 至 ${new Date(sub!.expiresAt).toLocaleDateString('zh-CN')}`
                : ''}
            </>
          ) : (
            ' · 额度用尽，请续购'
          )}
        </div>
      )}
      <p className="thesis-muted mb-4 text-sm">
        注册赠送约 {gift.quotaWan} 万 Token；额度不够再开通套餐。
        {isThesisCloudAuth() && (
          <span className="ml-1">{codepayOk ? '支持在线支付。' : '（支付通道未就绪时为演示开通）'}</span>
        )}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {THESIS_PLANS.filter((p) => p.id !== 'trial').map((plan) => (
          <div key={plan.id} className="thesis-panel relative flex flex-col p-4">
            {plan.recommended && (
              <Tag color="blue" className="absolute right-3 top-3">
                常用
              </Tag>
            )}
            <div className="text-base font-semibold">{plan.name}</div>
            <div className="mt-1 text-lg font-medium">{plan.priceLabel}</div>
            <p className="thesis-muted mt-2 text-sm">{plan.blurb}</p>
            <ul className="thesis-muted mt-3 flex-1 space-y-1 text-sm">
              {plan.highlights.map((h) => (
                <li key={h}>· {h}</li>
              ))}
            </ul>
            <Button
              type={plan.recommended ? 'primary' : 'default'}
              className="mt-4"
              block
              loading={buying === plan.id}
              onClick={() => void buy(plan.id)}
            >
              {sub?.planId === plan.id && active
                ? codepayOk
                  ? '续订'
                  : '续订（演示）'
                : codepayOk
                  ? '开通'
                  : '开通（演示）'}
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  )
}

export default PricingModal
